import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { useJsonLd, faqLd } from "@/hooks/useJsonLd";
import { fmtDate } from "@/pages/Blog";

const BASE = "https://gamerealm.org";

const md = {
  h2: (p) => <h2 className="font-display font-semibold tracking-tight text-xl sm:text-2xl mt-10 mb-3" {...p} />,
  h3: (p) => <h3 className="font-semibold text-lg mt-7 mb-2" {...p} />,
  p: (p) => <p className="text-gray-300 leading-relaxed my-4" {...p} />,
  ul: (p) => <ul className="list-disc pl-6 space-y-2 text-gray-300 my-4" {...p} />,
  ol: (p) => <ol className="list-decimal pl-6 space-y-2 text-gray-300 my-4" {...p} />,
  strong: (p) => <strong className="text-white" {...p} />,
  a: ({ href = "", children }) => {
    const local = href.startsWith(BASE) ? href.slice(BASE.length) || "/" : href.startsWith("/") ? href : null;
    return local
      ? <Link to={local} className="text-emerald-400 underline underline-offset-4 hover:text-emerald-300">{children}</Link>
      : <a href={href} target="_blank" rel="noopener noreferrer nofollow" className="text-emerald-400 underline underline-offset-4">{children}</a>;
  },
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setMissing(true)); }, [slug]);
  useSEO(post ? { title: post.title, description: post.meta_description, path: `/blog/${post.slug}` } : { path: `/blog/${slug}` });
  useJsonLd("ld-article", post && {
    "@context": "https://schema.org", "@type": "BlogPosting", headline: post.title, description: post.meta_description,
    datePublished: post.created_at, dateModified: post.created_at, keywords: post.keywords?.join(", "),
    author: { "@type": "Person", name: post.author }, publisher: { "@type": "Organization", name: "GameRealm", url: BASE },
    mainEntityOfPage: `${BASE}/blog/${post.slug}`,
  });
  useJsonLd("ld-faq", faqLd(post?.faq));

  if (missing) return <p data-testid="blog-not-found" className="text-center py-32 text-gray-400">Article not found.</p>;
  if (!post) return <div className="h-[60vh]" />;
  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-8 pt-10">
      <Link to="/blog" data-testid="back-to-blog-link" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white py-2.5"><ArrowLeft size={16} /> All articles</Link>
      <header className="mt-8 rise">
        <p className="text-xs text-gray-500">{fmtDate(post.created_at)} · by {post.author}</p>
        <h1 data-testid="blog-post-title" className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">{post.title}</h1>
      </header>
      <div data-testid="blog-post-body" className="mt-6"><ReactMarkdown components={md}>{post.body_markdown}</ReactMarkdown></div>
      {post.faq?.length > 0 && (
        <section data-testid="blog-post-faq" className="mt-12 rounded-xl border border-white/10 bg-[#0F1117] p-6 sm:p-8">
          <h2 className="font-display font-semibold text-xl mb-4">Quick answers</h2>
          <dl className="space-y-5">
            {post.faq.map((f) => <div key={f.q}><dt className="font-semibold">{f.q}</dt><dd className="text-gray-400 mt-1 text-sm leading-relaxed">{f.a}</dd></div>)}
          </dl>
        </section>
      )}
      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/" className="h-11 px-5 rounded-md bg-emerald-500 text-[#08090C] font-bold inline-flex items-center hover:bg-emerald-400">Browse game keys</Link>
        <Link to="/mystery" className="h-11 px-5 rounded-md border border-white/15 font-semibold inline-flex items-center hover:border-emerald-500/60">Try a mystery box</Link>
      </div>
    </article>
  );
}
